import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title:
		"「メイン画像の背景は白抜きにしてください」Amazon出品で弾かれた時の対処法",
	description:
		"Amazonセラーセントラルで「メイン画像の背景は白抜きにしてください」と弾かれる原因と、白背景にする具体的な手順を解説。RGB(255,255,255)の純白が必須。",
	publishDate: "2026-03-16",
	slug: "amazon-main-image-white-background-error",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function AmazonMainImageWhiteBackgroundError() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section className="mb-8">
						<p className="text-gray-700 leading-relaxed">
							商品を登録しようとしたら「メイン画像の背景は白抜きにしてください」と表示されて、出品できない——そんな経験、ありますよね。
							せっかく撮影した写真なのに、背景の色ひとつで弾かれてしまうのは本当に困ります。
							この記事では、このエラーが発生する原因と、今すぐ解決できる手順を詳しく解説します。
						</p>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							エラーが出る原因：Amazonのメイン画像は「純白」でなければならない
						</h2>
						<p className="text-gray-700 leading-relaxed mb-4">
							Amazonのメイン画像には、厳密なルールがあります。 背景色は{" "}
							<strong>RGB値 (255, 255, 255) の純白</strong>
							のみが許可されており、
							グレーがかった白・クリーム色・オフホワイトはすべてNGです。
						</p>
						<p className="text-gray-700 leading-relaxed mb-4">
							さらに以下の要件もすべて満たす必要があります。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4">
							<table className="w-full text-sm text-gray-700">
								<thead>
									<tr className="border-b">
										<th className="text-left py-2 font-semibold">項目</th>
										<th className="text-left py-2 font-semibold">要件</th>
									</tr>
								</thead>
								<tbody className="space-y-2">
									<tr className="border-b">
										<td className="py-2">背景色</td>
										<td className="py-2">純白 RGB(255, 255, 255) のみ</td>
									</tr>
									<tr className="border-b">
										<td className="py-2">解像度</td>
										<td className="py-2">
											長辺1,600px以上（推奨）、最大10,000px以下
										</td>
									</tr>
									<tr className="border-b">
										<td className="py-2">商品の占有率</td>
										<td className="py-2">画像全体の85%以上が商品</td>
									</tr>
									<tr className="border-b">
										<td className="py-2">ファイル形式</td>
										<td className="py-2">JPEG / TIFF / GIF</td>
									</tr>
									<tr>
										<td className="py-2">ファイルサイズ</td>
										<td className="py-2">5MB以下推奨</td>
									</tr>
								</tbody>
							</table>
						</div>
						<p className="text-gray-700 leading-relaxed">
							Amazonは自動で画像をスキャンしており、背景が純白でないと検索対象外に設定されます。
							発見されるまでに数時間〜24時間かかることもあるため、気づかないうちに売り上げが落ちていることもあります。
						</p>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">手動で解決する方法</h2>
						<p className="text-gray-700 leading-relaxed mb-4">
							以下の手順で純白背景の画像を作成できます。
						</p>
						<ol className="list-decimal list-inside space-y-4 text-gray-700">
							<li>
								<strong>背景を除去する</strong>
								<p className="mt-1 ml-5 text-sm">
									Photoshopの「選択範囲 →
									被写体を選択」またはGIMPの「ファジー選択」で背景を削除します。
									または remove.bg
									などのオンラインツールを使って背景を透明にします。
								</p>
							</li>
							<li>
								<strong>白背景レイヤーを追加する</strong>
								<p className="mt-1 ml-5 text-sm">
									透明化した画像の下に、RGB(255, 255,
									255)の新規レイヤーを追加します。
									グレー(250,250,250)などはNGです。必ず255,255,255を指定してください。
								</p>
							</li>
							<li>
								<strong>JPEGで書き出す</strong>
								<p className="mt-1 ml-5 text-sm">
									長辺を1,600px以上に設定し、JPEG形式で書き出します。
									ファイルサイズが5MBを超える場合は品質を下げて調整してください。
								</p>
							</li>
							<li>
								<strong>セラーセントラルで再アップロード</strong>
								<p className="mt-1 ml-5 text-sm">
									商品管理 → 「画像を管理」から差し替えます。
									変更が反映されるまで最大24時間かかることがあります。
								</p>
							</li>
						</ol>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							毎回手動でやるのは大変ですよね
						</h2>
						<p className="text-gray-700 leading-relaxed">
							写真を撮るたびにPhotoshopを開いて、背景を消して、白レイヤーを追加して……
							これを毎商品やるのは、正直かなりの手間です。
							<strong>しろぬきや</strong>
							なら、ブラウザ上にドラッグするだけでAI背景除去＋純白背景への変換が完了します。
						</p>
					</section>

					<CtaBlock />
					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
